// ── Accountability Scope Helpers ──
//
// Determines which roster people belong to the accountability scope
// (visitors/lobby track + badged-in + manual add) vs assigned-office/no-badge.

import {
  Incident,
  IncidentRosterPerson,
  IncidentCounts,
  IncidentSummary,
  HeadcountCountsDto,
  RosterScopeCountsDto,
} from '../../Types/Incident';

// ── Person-level classification ──

/** True when the person is an assigned-office employee who did not badge in today. */
export const isAssignedOfficeNoBadge = (person: IncidentRosterPerson): boolean => {
  // Manual-add and lobby-track people are always in accountability scope
  if (person.isUnlisted || person.rosterSource === 'manualAdd') return false;
  if (person.rosterSource === 'lobbyTrack') return false;
  if (person.inclusionReasons?.includes('lobbyTrack')) return false;
  if (person.inclusionReasons?.includes('manualAdd')) return false;

  // If API provides an explicit flag, trust it
  if (typeof person.isAssignedOfficeNoBadgeToday === 'boolean') return person.isAssignedOfficeNoBadgeToday;

  return person.isAssignedToIncidentOffice === true && person.hasIncidentDayBadgeActivity === false;
};

/** True when the person is included in the accountability denominator. */
export const isPersonInAccountabilityScope = (person: IncidentRosterPerson): boolean => {
  // If API provides an explicit flag, trust it
  if (typeof person.isInAccountabilityScope === 'boolean') return person.isInAccountabilityScope;
  return !isAssignedOfficeNoBadge(person);
};

/** True when the person should appear in the default headcount view. */
export const isPersonDefaultVisible = (person: IncidentRosterPerson): boolean => {
  // If API provides an explicit flag, trust it
  if (typeof person.isDefaultVisible === 'boolean') return person.isDefaultVisible;

  // Not Accounted people are ALWAYS visible (needs attention never hidden)
  if (person.status === 'notAccounted') return true;

  return isPersonInAccountabilityScope(person);
};

// ── Roster scope type ──

export type RosterScope = 'accountability' | 'assignedOfficeNoBadge' | 'full';

export const ROSTER_SCOPE_LABELS: Record<RosterScope, string> = {
  accountability: 'Accountability Roster',
  assignedOfficeNoBadge: 'Assigned Office / No Badge',
  full: 'Full Roster',
};

// ── Compute scope counts from roster data ──

const computeHeadcountCounts = (people: IncidentRosterPerson[]): HeadcountCountsDto => {
  let accounted = 0;
  let notAccounted = 0;
  let notChecked = 0;
  for (const p of people) {
    if (p.status === 'accounted') accounted++;
    else if (p.status === 'notAccounted') notAccounted++;
    else notChecked++;
  }
  const total = people.length;
  return {
    total,
    accounted,
    notAccounted,
    notChecked,
    percentComplete: total === 0 ? 0 : Math.round((accounted / total) * 100),
  };
};

/** Compute full roster scope counts from person-level roster data. */
export const computeRosterScopeCounts = (people: IncidentRosterPerson[]): RosterScopeCountsDto => {
  const accountabilityPeople: IncidentRosterPerson[] = [];
  const assignedNoBadgePeople: IncidentRosterPerson[] = [];

  for (const p of people) {
    if (isAssignedOfficeNoBadge(p)) {
      assignedNoBadgePeople.push(p);
    } else {
      accountabilityPeople.push(p);
    }
  }

  const fullRosterCounts = computeHeadcountCounts(people);
  const accountabilityCounts = computeHeadcountCounts(accountabilityPeople);

  return {
    accountabilityCounts,
    fullRosterCounts,
    assignedOfficeNoBadgeTodayCount: assignedNoBadgePeople.length,
    assignedOfficeNoBadgeTodayNotChecked: assignedNoBadgePeople.filter((p) => p.status === 'notChecked').length,
    needsAttentionCount: fullRosterCounts.notAccounted,
  };
};

// ── Derive scope counts from summary-level data (no roster available) ──

/** Derive approximate scope counts from IncidentCounts (incident list / dashboard). */
export const deriveScopeCountsFromIncidentCounts = (
  counts: IncidentCounts,
): RosterScopeCountsDto => {
  const fullRosterCounts: HeadcountCountsDto = {
    total: counts.total,
    accounted: counts.accounted,
    notAccounted: counts.notAccounted,
    notChecked: counts.notChecked,
    percentComplete: counts.percentComplete,
  };

  const assignedNoBadge = counts.assignedOfficeNotBadgedIn ?? 0;
  const accountabilityTotal = Math.max(0, counts.total - assignedNoBadge);
  // Accounted and notAccounted people are always attributed to accountability scope
  const accountabilityNotChecked = Math.max(0, accountabilityTotal - counts.accounted - counts.notAccounted);
  const accountabilityPercent = accountabilityTotal === 0 ? 0 : Math.round((counts.accounted / accountabilityTotal) * 100);

  const accountabilityCounts: HeadcountCountsDto = {
    total: accountabilityTotal,
    accounted: counts.accounted,
    notAccounted: counts.notAccounted,
    notChecked: accountabilityNotChecked,
    percentComplete: accountabilityPercent,
  };

  return {
    accountabilityCounts,
    fullRosterCounts,
    assignedOfficeNoBadgeTodayCount: assignedNoBadge,
    // Approximate: assigned-office/no-badge are mostly notChecked
    assignedOfficeNoBadgeTodayNotChecked: assignedNoBadge,
    needsAttentionCount: counts.notAccounted,
  };
};

/** Derive scope counts from IncidentSummary (dashboard page). */
export const deriveScopeCountsFromSummary = (
  summary: IncidentSummary,
): RosterScopeCountsDto => {
  // If API provides pre-computed scope counts, use them
  if (summary.scopeCounts) return summary.scopeCounts;

  const assignedNoBadge = summary.assignedOfficeNotBadgedIn ?? 0;
  const accountabilityTotal = Math.max(0, summary.total - assignedNoBadge);
  const accountabilityNotChecked = Math.max(0, accountabilityTotal - summary.accounted - summary.notAccounted);
  const accountabilityPercent = accountabilityTotal === 0 ? 0 : Math.round((summary.accounted / accountabilityTotal) * 100);

  return {
    accountabilityCounts: {
      total: accountabilityTotal,
      accounted: summary.accounted,
      notAccounted: summary.notAccounted,
      notChecked: accountabilityNotChecked,
      percentComplete: accountabilityPercent,
    },
    fullRosterCounts: {
      total: summary.total,
      accounted: summary.accounted,
      notAccounted: summary.notAccounted,
      notChecked: summary.notChecked,
      percentComplete: summary.percentComplete,
    },
    assignedOfficeNoBadgeTodayCount: assignedNoBadge,
    assignedOfficeNoBadgeTodayNotChecked: assignedNoBadge,
    needsAttentionCount: summary.notAccounted,
  };
};

// ── Accessor helpers (prefer API-provided scope counts) ──

export const getAccountabilityCounts = (scope: RosterScopeCountsDto): HeadcountCountsDto =>
  scope.accountabilityCounts;

export const getFullRosterCounts = (scope: RosterScopeCountsDto): HeadcountCountsDto =>
  scope.fullRosterCounts;

export const getDefaultViewCounts = (scope: RosterScopeCountsDto): HeadcountCountsDto =>
  scope.defaultViewCounts ?? scope.accountabilityCounts;

export const getAssignedOfficeNoBadgeCount = (scope: RosterScopeCountsDto): number =>
  scope.assignedOfficeNoBadgeTodayCount ?? 0;

export const getAssignedOfficeNoBadgeNotCheckedCount = (scope: RosterScopeCountsDto): number =>
  scope.assignedOfficeNoBadgeTodayNotChecked ?? 0;

export const getNeedsAttentionCount = (scope: RosterScopeCountsDto): number =>
  scope.needsAttentionCount ?? scope.fullRosterCounts.notAccounted;

// ── Filter roster by scope ──

export const filterRosterByScope = (
  people: IncidentRosterPerson[],
  scope: RosterScope,
): IncidentRosterPerson[] => {
  switch (scope) {
    case 'accountability':
      return people.filter((p) => isPersonDefaultVisible(p));
    case 'assignedOfficeNoBadge':
      return people.filter((p) => isAssignedOfficeNoBadge(p));
    case 'full':
      return people;
  }
};

// ── Incident list helpers ──

/** Resolve scope counts for an incident list item, preferring API-provided scopeCounts. */
export const getIncidentScopeCounts = (incident: Incident): RosterScopeCountsDto =>
  incident.scopeCounts ?? deriveScopeCountsFromIncidentCounts(incident.currentCounts);

/** Accountability counts for incident list display (percent, denominator). */
export const getIncidentListAccountabilityCounts = (incident: Incident): HeadcountCountsDto =>
  getIncidentScopeCounts(incident).accountabilityCounts;

/** Full roster counts for secondary context. */
export const getIncidentListFullRosterCounts = (incident: Incident): HeadcountCountsDto =>
  getIncidentScopeCounts(incident).fullRosterCounts;

/** Needs Attention / Not Accounted count across all scopes. */
export const getIncidentListNeedsAttentionCount = (incident: Incident): number =>
  getNeedsAttentionCount(getIncidentScopeCounts(incident));

/** Assigned Office / No Badge count. */
export const getIncidentListAssignedOfficeNoBadgeCount = (incident: Incident): number =>
  getAssignedOfficeNoBadgeCount(getIncidentScopeCounts(incident));
